import axios from "axios";
import React, { useEffect, useState, memo, useRef } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Loader from "../../../../components/Loader";
import Table from "react-bootstrap/Table";
import { useTranslation } from "react-i18next";
import { getBankDetails } from "../../../../redux/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { specialCharacterTest } from "common/Regex";
import { OTPModalUI } from "./OTPModalUI";
import { mobileScroll } from "common/FunctionUtils";
import { LOCALES } from "common/constants";

const BankInformation = () => {
  const dispatch = useDispatch();
  const { isLoading, bankDetails } = useSelector((details) => details.auth);

  // --------------------------------Translator--------------------------------------

  const { t } = useTranslation();
  const language = localStorage.getItem("i18nextLng");

  // --------------------------------UseState--------------------------------------
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hideNote, setHideNote] = useState(false);

  const inputRefs = useRef([]);
  const [OTPModal, setOTPModal] = useState(false);
  const [OTPField, setOTPField] = useState(new Array(4).fill(""));
  const [counter, setCounter] = useState(300);

  // --------------------------------UseForm--------------------------------------

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  // --------------------------------Watch-UseForm--------------------------------------

  const bankName = watch("bank");

  // --------------------------------Get Bank Details--------------------------------------

  useEffect(() => {
    dispatch(getBankDetails());
  }, [dispatch]);

  useEffect(() => {
    bankDetails.forEach((data) => {
      reset({ ...data });
    });
    setData(bankDetails[0]);
  }, [bankDetails, reset]);

  // --------------------------------Edit Bank Details--------------------------------------

  const [toggle, setToggle] = useState(false);
  const [bankValues, setBankValues] = useState();

  const generateOTP = async () => {
    setLoading(true);
    try {
      const URL = "/settings/generate-otp";

      const body = {
        type: 3,
        email: "",
        lang: language,
      };

      const result = await axios.post(URL, body);
      if (result?.status === 200) {
        setLoading(false);
        toast.success(t("Email has been successfully sent to your email"));
        setOTPModal(true);
      }
    } catch (error) {
      setLoading(false);
      toast.error(t(error?.response?.data?.message));
    }
  };

  const onSubmit = async (values) => {
    setToggle(false);
    setBankValues(values);
    generateOTP();
  };

  useEffect(() => {
    if (bankName == "Foreign bank BIC / IBAN") {
      setHideNote(true);
    } else {
      setHideNote(false);
    }
  }, [bankName]);

  const handleClose = () => setToggle(false);

  const handleShow = () => {
    setToggle(true);
  };

  let isValidated;
  const validateClearingNumber = (value) => {
    isValidated = specialCharacterTest(value);
    if (!isValidated) {
      setError("clearingNumber", {
        type: "manual",
        message: `${t(
          "Please do not enter hyphen and any other special character"
        )}`,
      });
    } else {
      clearErrors("clearingNumber");
    }
    return true;
  };

  const validateAccountNumber = (value) => {
    isValidated = specialCharacterTest(value);
    if (!isValidated) {
      setError("accountNumber", {
        type: "manual",
        message: `${t(
          "Please do not enter hyphen and any other special character"
        )}`,
      });
    } else {
      clearErrors("accountNumber");
    }
    return true;
  };

  const handleChangeOTP = async (index, e) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...OTPField];

    newOtp[index] = value.substring(value.length - 1);
    setOTPField(newOtp);

    if (value && index < 4 - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
    if (value && index === 3) {
      const combinedOTP = newOtp.join("");

      try {
        setLoading(true);
        const URL = "/settings/save-customer-salary";
        const payload = {
          id: data?.id,
          ...bankValues,
          incomeTax: bankValues.incomeTax,
          accountNumber: bankValues.accountNumber
            ? bankValues.accountNumber
            : "",
          clearingNumber: bankValues.clearingNumber
            ? bankValues.clearingNumber
            : "",
          bank:
            bankValues.bank === "Other Bank"
              ? bankValues.OtherBank
              : bankValues.bank,
          code: combinedOTP,
        };
        const result = await axios.put(URL, payload);
        if (result?.status === 200) {
          await dispatch(getBankDetails());
          toast.success(t(result?.data?.message));
          setOTPModal(false);
          setOTPField(new Array(4).fill(""));
        } else {
          toast.error(t("Something went wrong please try again."));
        }
      } catch (err) {
        if (err?.response?.data) {
          toast.error(t(err?.response?.data?.message));
        } else {
          toast.error(t(err?.message));
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const resendOTP = () => {
    setOTPField(new Array(4).fill(""));
    generateOTP();
  };

  useEffect(() => {
    mobileScroll();
  }, []);

  return (
    <div className="page-content SalaryInfromation mt-5 invoice-space2 info-space salry_respnv">
      <Helmet>
        {language === LOCALES.EN_US ? (
          <link
            rel="canonical"
            href="https://invozio.com/dashboard/settings/bank-information"
          />
        ) : (
          <link
            rel="canonical"
            href="https://invozio.com/instrumentbrada/installningar/bankinformation"
          />
        )}
      </Helmet>
      {(isLoading || loading) && <Loader />}

      <Modal show={toggle} onHide={handleClose} animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>Bank Information</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("Are you sure you want to update your bank account information?")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {t("No")}
          </Button>
          <Button
            variant="primary"
            className="theme_btn"
            onClick={handleSubmit(onSubmit)}
          >
            {/* handleSubmit(onSubmit) */}
            {t("Yes")}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="settings">
        <form onSubmit={handleSubmit(handleShow)} className="bank-info">
          <div className="slary_allowance">
            <h4 className="accident_ins font-weight-bold">
              {t("Salary and allowance")}
            </h4>
            <h3>
              <span>{t("Bank information")}</span>
              <p className="mb-0" style={{ fontSize: "14px" }}>
                {t(
                  "We expressly disclaim any responsibility for discrepancies or inaccuracies in the bank account details furnished by the user."
                )}
              </p>
            </h3>
          </div>
          <div className="section">
            <div className="mb-3 position-relative">
              <label htmlFor="inputAddress">Bank</label>
              <select
                className="form-control styled-select select w-100"
                {...register("bank")}
              >
                <option> {t("Select a bank")} </option>
                <option value="Handelsbanken"> {t("Handelsbanken")} </option>
                <option value="Nordea social security number">
                  {" "}
                  {t("Nordea social security number")}
                </option>
                <option value="Nordea Account number">
                  {" "}
                  {t("Nordea Account number")}{" "}
                </option>
                <option value="Swedbank"> Swedbank </option>
                <option value="SEB"> SEB </option>
                <option value="Foreign bank BIC / IBAN">
                  {" "}
                  {t("Foreign bank BIC / IBAN")}{" "}
                </option>
                <option value="Other Bank"> {t("Other Bank")} </option>
                {data?.bank !==
                  ("Handelsbanken" ||
                    "Nordea social security number" ||
                    "Nordea Account number" ||
                    "Swedbank" ||
                    "SEB" ||
                    "Foreign bank BIC / IBAN") && (
                  <option value={data?.bank}> {data?.bank} </option>
                )}
              </select>

              {hideNote && (
                <p className="mb-0 note-text">
                  {t(
                    "Note: If you choose a foreign bank account, 350 SEK will be deducted from your salary."
                  )}
                </p>
              )}
            </div>

            {bankName === "Other Bank" && (
              <div className="mb-3 position-relative">
                <label
                  htmlFor="Bank-Details"
                  className="form-control-placeholder"
                >
                  <span>Bank Name</span>
                </label>
                <input
                  {...register("OtherBank")}
                  id="Bank-Details"
                  autoFocus
                  placeholder="Your Bank Name Here"
                  type="text"
                  className="form-control styled-select select w-100 mb-4"
                />
              </div>
            )}

            <div className="form-row m-0 justify-content-between flex-xs-column">
              {bankName === "Foreign bank BIC / IBAN" ? (
                <>
                  <div className="form-group input-wrapper mb-0">
                    <input
                      autoFocus
                      type="text"
                      className={`form-control ${
                        errors.ibanNumber ? "is-invalid" : ""
                      }`}
                      id="ibanNumber"
                      {...register("ibanNumber", {
                        required: bankName === "Foreign bank BIC / IBAN",
                      })}
                    />
                    <label
                      className="form-control-placeholder"
                      htmlFor="ibanNumber"
                    >
                      {t("IBAN Number")}
                    </label>
                  </div>
                  <div className="form-group input-wrapper mb-0">
                    <input
                      className={`form-control border-radius-0 w-100 ${
                        errors.bicNumber ? "is-invalid" : ""
                      }`}
                      type="text"
                      id="bicNumber"
                      {...register("bicNumber", {
                        required: bankName === "Foreign bank BIC / IBAN",
                      })}
                    />
                    <label
                      className="form-control-placeholder"
                      htmlFor="bicNumber"
                      style={{ zIndex: "1" }}
                    >
                      {t("BIC Number")}
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group input-wrapper mb-0">
                    <label htmlFor="inputCity"> {t("Clearing number")}</label>
                    <input
                      className={`form-control border-radius-0 w-100 ${
                        errors.clearingNumber ? "is-invalid" : ""
                      }`}
                      type="number"
                      id="clearingNumber"
                      {...register("clearingNumber", {
                        required: bankName !== "Foreign bank BIC / IBAN",
                        validate: validateClearingNumber,
                      })}
                      onInput={(e) => {
                        let value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
                        if (value.length > 4) {
                          value = value.substring(0, 4); // Limit to 4 digits
                        }
                        e.target.value = value;
                        validateClearingNumber(e.target.value);
                      }}
                    />
                    {errors.clearingNumber && (
                      <div className="invalid-feedbackk">
                        {errors.clearingNumber.message}
                      </div>
                    )}
                    {/*  */}
                  </div>

                  <div className="form-group input-wrapper mb-0">
                    <label htmlFor="inputState"> {t("Account number")}</label>
                    <input
                      type="number"
                      className={`form-control ${
                        errors.accountNumber ? "is-invalid" : ""
                      }`}
                      id="accountNumber"
                      {...register("accountNumber", {
                        required: bankName !== "Foreign bank BIC / IBAN",
                        validate: validateAccountNumber,
                      })}
                      onInput={(e) => {
                        let value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
                        if (value.length > 10) {
                          value = value.substring(0, 10); // Limit to 10 digits
                        }
                        e.target.value = value;
                        validateAccountNumber(e.target.value);
                      }}
                    />
                    {errors.accountNumber && (
                      <div className="invalid-feedbackk">
                        {errors.accountNumber.message}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {bankName !== "Foreign bank BIC / IBAN" && (
              <p className="mb-0 note-validate-text">
                {t(
                 "Note: The clearing number should be 4 digits (for Swedbank accounts, please remove the last digit from the clearing number) and account number can have maximum 10 digits. Special characters are not allowed."
                )}
              </p>
            )}
          </div>

          <div className="button-wrapper mb-4">
            <Button
              disabled={errors.clearingNumber || errors.accountNumber}
              type="submit"
              className="text-light save_add_customer new_theme_btn"
            >
              <span>{t("Save Settings")}</span>
            </Button>
          </div>
        </form>
      </div>
      <div className="data-table">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>{t("Bank Name")}</th>
              <th>
                {bankName === "Foreign bank BIC / IBAN" && data?.ibanNumber
                  ? t("IBAN Number")
                  : t("Clearing number")}
              </th>
              <th>
                {bankName === "Foreign bank BIC / IBAN" && data?.bicNumber
                  ? t("BIC Number")
                  : t("Account number")}
              </th>
              <th>{t("Income Tax")}</th>
              <th>{t("Created Date")}</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>{t(data?.bank)}</td>
              <td>
                {bankName === "Foreign bank BIC / IBAN" && data?.ibanNumber
                  ? data?.ibanNumber
                  : data?.clearingNumber}
              </td>
              <td>
                {bankName === "Foreign bank BIC / IBAN" && data?.bicNumber
                  ? data?.bicNumber
                  : data?.accountNumber}
              </td>
              <td>
                {data?.incomeTax}
                {data?.incomeTax ? "%" : ""}
              </td>
              <td>{data?.createdDate?.slice(0, 10)}</td>
            </tr>
          </tbody>
        </Table>
      </div>
      <OTPModalUI
        OTPModal={OTPModal}
        setOTPModal={setOTPModal}
        resendOTP={resendOTP}
        OTPField={OTPField}
        setOTPField={setOTPField}
        inputRefs={inputRefs}
        handleChangeOTP={handleChangeOTP}
        counter={counter}
        setCounter={setCounter}
      />
    </div>
  );
};

export default memo(BankInformation);
